<template>
  <div class="lead-review-list">
    <el-card shadow="never" class="search-card">
      <el-form :model="searchForm" label-width="80px" class="search-form" inline>
        <el-form-item label="关键字">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入姓名/手机号/线索ID"
            clearable
            class="search-input"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="手机号">
          <el-radio-group v-model="searchForm.phonePresence" size="small">
            <el-radio-button label="HAS">有</el-radio-button>
            <el-radio-button label="EMPTY">无</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核状态">
          <el-select v-model="searchForm.reviewStatus" placeholder="请选择审核状态" clearable class="search-select">
            <el-option label="待审核" value="WAITING" />
            <el-option label="已通过" value="APPROVED" />
            <el-option label="无效拒绝" value="REJECTED_INVALID" />
            <el-option label="信息不完整" value="REJECTED_INCOMPLETE" />
          </el-select>
        </el-form-item>
        <el-form-item label="线索时间">
          <el-date-picker
            v-model="searchForm.leadTimeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="leadTimePickerOptions"
            class="search-datetime-range"
            align="right"
          />
        </el-form-item>
        <el-form-item label-width="0" class="search-form-actions">
          <el-button type="primary" icon="el-icon-search" :loading="searchLoading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" :loading="resetLoading" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="table-card">
      <el-table
        ref="reviewTable"
        :data="tableData"
        border
        stripe
        size="small"
        height="100%"
        v-loading="loading"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <template v-for="col in activeTableColumns">
          <el-table-column
            v-if="col.key === 'conversationSummary'"
            :key="col.key"
            :prop="col.prop"
            :label="col.label"
            :min-width="col.minWidth"
            align="center"
          >
            <template slot-scope="{ row }">
              <div v-if="parseSummaryTags(row.conversationSummary).length" class="summary-tag-list">
                <el-tag
                  v-for="(tag, index) in parseSummaryTags(row.conversationSummary)"
                  :key="`${row.id || row.clueId || 'row'}-${index}-${tag}`"
                  size="mini"
                  effect="plain"
                  class="summary-tag"
                  @click.native="openConversation(row, tag)"
                >
                  {{ tag }}
                </el-tag>
              </div>
              <span v-else class="summary-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column
            v-else-if="col.key === 'reviewStatus'"
            :key="col.key"
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            align="center"
          >
            <template slot-scope="{ row }">
              <el-tag size="small" :type="tagType(row.reviewStatus)">{{ tagLabel(row.reviewStatus) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column
            v-else
            :key="col.key"
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            :min-width="col.minWidth"
            show-overflow-tooltip
            align="center"
          />
        </template>
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template slot="header">
            <div class="operation-header-cell">
              <span>操作</span>
              <el-button type="text" icon="el-icon-s-tools" class="column-config-trigger" @click.stop="openColumnConfigDialog" />
            </div>
          </template>
          <template slot-scope="{ row }">
            <el-button type="text" @click="openConversation(row)">会话详情</el-button>
            <el-button type="text" @click="openDetail(row)">处理</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="page"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          @size-change="handleSizeChange"
          @current-change="fetchList"
        />
      </div>
    </el-card>

    <el-dialog title="自定义列配置" :visible.sync="columnConfigVisible" width="520px" :close-on-click-modal="false">
      <div class="column-config-tip">拖动可调整列顺序；序号与操作栏固定展示。</div>
      <div class="column-config-list">
        <div
          v-for="(col, index) in columnConfigDraft"
          :key="col.key"
          class="column-config-item"
          draggable="true"
          @dragstart="handleColumnDragStart(index)"
          @dragover.prevent
          @drop="handleColumnDrop(index)"
        >
          <el-checkbox v-model="col.visible">{{ getColumnLabel(col.key) }}</el-checkbox>
          <i class="el-icon-d-caret column-drag-icon"></i>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="resetColumnConfigDraft">恢复默认列</el-button>
        <el-button @click="columnConfigVisible = false">取消</el-button>
        <el-button type="primary" @click="saveColumnConfig">保存</el-button>
      </span>
    </el-dialog>

    <baidu-lead-detail-drawer
      :visible.sync="detailVisible"
      :detail="detailData"
      :conversation-visible.sync="detailConversationVisible"
      :conversation-loading="conversationLoading"
      :conversation="conversationData"
      @close="detailVisible = false"
      @open-conversation="openDetailConversation"
      @close-conversation="detailConversationVisible = false"
      @approve="handleApprove"
      @reject="handleReject"
      @link-student="handleLinkStudent"
    />
    <baidu-conversation-dialog
      :visible.sync="conversationVisible"
      :loading="conversationLoading"
      :conversation="conversationData"
      :highlight-text="conversationHighlightText"
      @close="conversationVisible = false"
    />
  </div>
</template>

<script>
import {
  fetchBaiduAifanfanLeadList,
  fetchBaiduAifanfanLeadDetail,
  fetchBaiduAifanfanLeadConversation,
  fetchBaiduAifanfanLeadColumns,
  saveBaiduAifanfanLeadColumns,
  approveBaiduAifanfanLead,
  rejectBaiduAifanfanLead,
  linkBaiduAifanfanLeadStudent
} from '@/services/baiduAifanfanApi';
import BaiduLeadDetailDrawer from './BaiduLeadDetailDrawer.vue';
import BaiduConversationDialog from './BaiduConversationDialog.vue';

function pad(value) {
  return String(value).padStart(2, '0');
}

function formatDateTime(date) {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-') + ' ' + [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join(':');
}

function getDayRange(daysBeforeStart = 0, daysBeforeEnd = 0) {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - daysBeforeStart);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setDate(now.getDate() - daysBeforeEnd);
  end.setHours(23, 59, 59, 999);
  return [formatDateTime(start), formatDateTime(end)];
}

function getMonthRange(monthsBefore = 0) {
  const now = new Date();
  const start = new Date(now);
  start.setMonth(now.getMonth() - monthsBefore);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return [formatDateTime(start), formatDateTime(end)];
}

function getYearRange() {
  const now = new Date();
  const start = new Date(now);
  start.setFullYear(now.getFullYear() - 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return [formatDateTime(start), formatDateTime(end)];
}

function getTodayRange() {
  return getDayRange(0, 0);
}

const ALL_TABLE_COLUMNS = [
  { key: 'planName', label: '推广计划名称', prop: 'planName', minWidth: 140 },
  { key: 'unitName', label: '推广单元名称', prop: 'unitName', minWidth: 140 },
  { key: 'keyword', label: '关键词', prop: 'keyword', minWidth: 130 },
  { key: 'searchWord', label: '搜索词', prop: 'searchWord', minWidth: 130 },
  { key: 'phoneCandidate', label: '手机号', prop: 'phoneCandidate', width: 110 },
  { key: 'nameCandidate', label: '学员姓名', prop: 'nameCandidate', width: 110 },
  { key: 'projectCandidate', label: '咨询科目', prop: 'projectCandidate', minWidth: 140 },
  { key: 'conversationSummary', label: '会话摘要', prop: 'conversationSummary', minWidth: 260 },
  { key: 'commitTime', label: '线索时间', prop: 'commitTime', width: 160 },
  { key: 'sourceType', label: '来源类型', prop: 'sourceType', width: 120 },
  { key: 'solutionTypeName', label: '业务类型', prop: 'solutionTypeName', width: 100 },
  { key: 'areaProvince', label: '省/直辖市', prop: 'areaProvince', width: 120 },
  { key: 'area', label: '城市/地区', prop: 'area', width: 120 },
  { key: 'reviewStatus', label: '审核状态', prop: 'reviewStatus', width: 100 },
  { key: 'reviewerName', label: '审核人', prop: 'reviewerName', width: 110 },
  { key: 'reviewedAt', label: '审核时间', prop: 'reviewedAt', width: 150 }
];

const DEFAULT_COLUMN_CONFIG = [
  { key: 'planName', visible: true },
  { key: 'unitName', visible: true },
  { key: 'keyword', visible: true },
  { key: 'searchWord', visible: true },
  { key: 'phoneCandidate', visible: true },
  { key: 'nameCandidate', visible: true },
  { key: 'projectCandidate', visible: true },
  { key: 'conversationSummary', visible: true },
  { key: 'commitTime', visible: true },
  { key: 'sourceType', visible: false },
  { key: 'solutionTypeName', visible: false },
  { key: 'areaProvince', visible: false },
  { key: 'area', visible: false },
  { key: 'reviewStatus', visible: false },
  { key: 'reviewerName', visible: false },
  { key: 'reviewedAt', visible: false }
];

export default {
  name: 'BaiduLeadReviewList',
  components: { BaiduLeadDetailDrawer, BaiduConversationDialog },
  data() {
    return {
      loading: false,
      searchLoading: false,
      resetLoading: false,
      page: 1,
      pageSize: 10,
      total: 0,
      tableData: [],
      detailVisible: false,
      detailData: null,
      conversationVisible: false,
      detailConversationVisible: false,
      conversationLoading: false,
      conversationData: {
        clueId: '',
        keyword: '',
        searchWord: '',
        messages: []
      },
      conversationHighlightText: '',
      currentLeadId: '',
      columnConfigVisible: false,
      draggingColumnIndex: -1,
      allTableColumns: ALL_TABLE_COLUMNS,
      defaultColumnConfigList: DEFAULT_COLUMN_CONFIG,
      columnConfigList: DEFAULT_COLUMN_CONFIG.map(item => ({ ...item })),
      columnConfigDraft: [],
      searchForm: {
        keyword: '',
        phonePresence: 'HAS',
        reviewStatus: 'WAITING',
        leadTimeRange: getTodayRange()
      }
    };
  },
  computed: {
    leadTimePickerOptions() {
      return {
        shortcuts: [
          { text: '昨天', onClick: (picker) => picker.$emit('pick', getDayRange(1, 1).map((item) => new Date(item))) },
          { text: '前天', onClick: (picker) => picker.$emit('pick', getDayRange(2, 2).map((item) => new Date(item))) },
          { text: '近三天', onClick: (picker) => picker.$emit('pick', getDayRange(2, 0).map((item) => new Date(item))) },
          { text: '近一周', onClick: (picker) => picker.$emit('pick', getDayRange(6, 0).map((item) => new Date(item))) },
          { text: '近一个月', onClick: (picker) => picker.$emit('pick', getMonthRange(1).map((item) => new Date(item))) },
          { text: '近三个月', onClick: (picker) => picker.$emit('pick', getMonthRange(3).map((item) => new Date(item))) },
          { text: '近一年', onClick: (picker) => picker.$emit('pick', getYearRange().map((item) => new Date(item))) }
        ]
      };
    },
    activeTableColumns() {
      const columnMap = this.allTableColumns.reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
      }, {});
      return this.columnConfigList
        .filter(item => item.visible && columnMap[item.key])
        .map(item => columnMap[item.key]);
    }
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      await this.loadColumnConfig();
      await this.fetchList();
    },
    syncTableLayout() {
      this.$nextTick(() => {
        const tableRef = this.$refs.reviewTable;
        if (tableRef && typeof tableRef.doLayout === 'function') tableRef.doLayout();
      });
    },
    normalizeColumnConfig(configList) {
      const allKeys = this.allTableColumns.map(item => item.key);
      const defaultMap = this.defaultColumnConfigList.reduce((acc, item) => {
        acc[item.key] = !!item.visible;
        return acc;
      }, {});
      const seen = new Set();
      const merged = [];
      (Array.isArray(configList) ? configList : []).forEach(item => {
        if (!item || !allKeys.includes(item.key) || seen.has(item.key)) return;
        seen.add(item.key);
        merged.push({ key: item.key, visible: typeof item.visible === 'boolean' ? item.visible : !!defaultMap[item.key] });
      });
      this.defaultColumnConfigList.forEach(item => {
        if (!seen.has(item.key)) {
          seen.add(item.key);
          merged.push({ key: item.key, visible: !!item.visible });
        }
      });
      this.allTableColumns.forEach(item => {
        if (!seen.has(item.key)) merged.push({ key: item.key, visible: !!defaultMap[item.key] });
      });
      return merged;
    },
    getColumnLabel(key) {
      const target = this.allTableColumns.find(item => item.key === key);
      return target ? target.label : key;
    },
    async loadColumnConfig() {
      try {
        const res = await fetchBaiduAifanfanLeadColumns();
        this.columnConfigList = this.normalizeColumnConfig(res && res.success ? res.data || [] : []);
      } catch (e) {
        this.columnConfigList = this.normalizeColumnConfig([]);
      }
    },
    openColumnConfigDialog() {
      this.columnConfigDraft = this.columnConfigList.map(item => ({ ...item }));
      this.columnConfigVisible = true;
    },
    resetColumnConfigDraft() {
      this.columnConfigDraft = this.defaultColumnConfigList.map(item => ({ ...item }));
    },
    handleColumnDragStart(index) {
      this.draggingColumnIndex = index;
    },
    handleColumnDrop(index) {
      const fromIndex = this.draggingColumnIndex;
      this.draggingColumnIndex = -1;
      if (fromIndex < 0 || fromIndex === index) return;
      const moved = this.columnConfigDraft.splice(fromIndex, 1)[0];
      this.columnConfigDraft.splice(index, 0, moved);
    },
    async saveColumnConfig() {
      const normalized = this.normalizeColumnConfig(this.columnConfigDraft);
      try {
        const res = await saveBaiduAifanfanLeadColumns(normalized);
        if (!res || !res.success) {
          this.$message.error((res && res.message) || '保存失败');
          return;
        }
        this.columnConfigList = normalized;
        this.columnConfigVisible = false;
        this.$message.success('自定义列已保存');
        this.syncTableLayout();
      } catch (e) {
        this.$message.error('保存失败');
      }
    },
    async fetchList(options = {}) {
      const { searchLoading = false, resetLoading = false } = options || {};
      this.loading = true;
      this.searchLoading = searchLoading;
      this.resetLoading = resetLoading;
      try {
        const [startTime, endTime] = Array.isArray(this.searchForm.leadTimeRange)
          ? this.searchForm.leadTimeRange
          : [];
        const res = await fetchBaiduAifanfanLeadList({
          keyword: this.searchForm.keyword,
          phonePresence: this.searchForm.phonePresence,
          reviewStatus: this.searchForm.reviewStatus,
          startTime,
          endTime,
          page: this.page,
          pageSize: this.pageSize
        });
        this.tableData = res.data.list || [];
        this.total = res.data.total || 0;
        this.syncTableLayout();
      } finally {
        this.loading = false;
        this.searchLoading = false;
        this.resetLoading = false;
      }
    },
    async applyReviewStatusFilter(status) {
      this.searchForm.reviewStatus = status;
      this.page = 1;
      await this.fetchList();
    },
    async applyKeywordFilter(keyword) {
      this.searchForm.keyword = String(keyword || '').trim();
      this.searchForm.phonePresence = 'HAS';
      this.page = 1;
      await this.fetchList({ searchLoading: true });
    },
    openTodayLeads() {
      this.searchForm.reviewStatus = '';
      this.searchForm.phonePresence = 'HAS';
      this.searchForm.leadTimeRange = getTodayRange();
      this.page = 1;
      this.fetchList();
    },
    handleSearch() {
      this.page = 1;
      this.fetchList({ searchLoading: true });
    },
    resetSearch() {
      this.searchForm.keyword = '';
      this.searchForm.phonePresence = 'HAS';
      this.searchForm.reviewStatus = 'WAITING';
      this.searchForm.leadTimeRange = getTodayRange();
      this.page = 1;
      this.fetchList({ resetLoading: true });
    },
    async handleSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.page = 1;
      this.fetchList();
    },
    tagLabel(status) {
      return {
        WAITING: '待审核',
        APPROVED: '已通过',
        REJECTED_INVALID: '无效拒绝',
        REJECTED_DUPLICATED: '重复线索',
        REJECTED_INCOMPLETE: '信息不完整'
      }[status] || status;
    },
    tagType(status) {
      return {
        WAITING: 'warning',
        APPROVED: 'success',
        REJECTED_INVALID: 'danger',
        REJECTED_DUPLICATED: 'info',
        REJECTED_INCOMPLETE: 'info'
      }[status] || '';
    },
    parseSummaryTags(summary) {
      return String(summary || '')
        .split(/[、,，;；\n]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    },
    async openDetail(row) {
      this.currentLeadId = row.id;
      this.detailConversationVisible = false;
      const res = await fetchBaiduAifanfanLeadDetail(row.id);
      this.detailData = res.data;
      this.detailVisible = true;
    },
    async openConversation(row, highlightText = '') {
      this.currentLeadId = row.id;
      this.conversationHighlightText = String(highlightText || '').trim();
      this.conversationVisible = true;
      const loaded = await this.loadConversation(row.id, row.clueId);
      if (!loaded) this.conversationVisible = false;
    },
    async openDetailConversation() {
      if (!this.detailData || !this.detailData.lead) return;
      const leadId = this.currentLeadId || this.detailData.lead.id;
      const clueId = this.detailData.lead.clueId;
      this.conversationHighlightText = '';
      this.detailConversationVisible = true;
      const loaded = await this.loadConversation(leadId, clueId);
      if (!loaded) this.detailConversationVisible = false;
    },
    async loadConversation(id, clueId) {
      this.conversationLoading = true;
      this.conversationData = { clueId, keyword: '', searchWord: '', messages: [] };
      try {
        const res = await fetchBaiduAifanfanLeadConversation(id);
        this.conversationData = res.data || { clueId, keyword: '', searchWord: '', messages: [] };
        return true;
      } catch (error) {
        this.$message.error((error && error.message) || '会话详情加载失败');
        return false;
      } finally {
        this.conversationLoading = false;
      }
    },
    async handleApprove(payload) {
      await approveBaiduAifanfanLead(this.currentLeadId, payload);
      this.$message.success('审核通过成功');
      this.detailVisible = false;
      this.detailConversationVisible = false;
      this.fetchList();
      this.$emit('refresh-overview');
    },
    async handleReject(payload) {
      await rejectBaiduAifanfanLead(this.currentLeadId, payload);
      this.$message.success('审核拒绝成功');
      this.detailVisible = false;
      this.detailConversationVisible = false;
      this.fetchList();
      this.$emit('refresh-overview');
    },
    async handleLinkStudent(payload) {
      await linkBaiduAifanfanLeadStudent(this.currentLeadId, payload);
      this.$message.success('关联学员成功');
      this.detailVisible = false;
      this.detailConversationVisible = false;
      this.fetchList();
      this.$emit('refresh-overview');
    }
  }
};
</script>

<style scoped>
.lead-review-list {
  min-height: 100%;
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
}

.search-card {
  margin-bottom: 16px;
}

.search-form {
  margin-bottom: -22px;
}

.search-input {
  width: 220px;
}

.table-card {
  flex: 1;
  min-height: 0;
}

.table-card :deep(.el-card__body) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-card :deep(.el-table) {
  flex: 1;
  min-height: 0;
  max-height: calc(100% - 37px);
}

.search-select,
.search-datetime-range {
  width: 100%;
}

.summary-tag-list {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 70px;
  overflow-y: auto;
}

.summary-tag {
  cursor: pointer;
  max-width: 100%;
}

.summary-tag:hover {
  color: white;
  background-color: #47a1ff;
}

.summary-tag :deep(.el-tag__content),
.summary-tag {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-empty {
  color: #c0c4cc;
}

.operation-header-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.column-config-trigger {
  padding: 0;
  font-size: 16px;
}

.column-config-tip {
  margin-bottom: 10px;
  color: #909399;
  font-size: 13px;
}

.column-config-list {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 10px;
}

.column-config-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px dashed #f0f0f0;
  cursor: move;
}

.column-config-item:last-child {
  border-bottom: none;
}

.column-drag-icon {
  margin-left: auto;
  color: #909399;
  cursor: move;
}

.pagination-wrapper {
  flex: 0 0 auto;
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>
